import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicGet } from '../services/apiService';
import { getFullUrl } from '../config/apiConfig';
import { 
  Calendar, 
  User, 
  Tag, 
  Search,
  ArrowRight,
  Home
} from 'lucide-react';

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'title'
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 9;

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const url = getFullUrl('blogs');
      
      const response = await publicGet(url);
      
      if (response.status === 404) {
        console.error('Blog list endpoint not found (404)');
        setBlogs([]);
        setLoading(false);
        return;
      }
      
      let blogsData;
      
      // Check if response is JSON or HTML
      const contentType = response.headers.get('content-type');
    
      
      if (contentType && contentType.includes('application/json')) {
        // Parse JSON response
        const jsonResponse = await response.json();
    
        
        // Handle the nested structure: { success: true, data: { blogs: [...], total: 2 } }
        if (jsonResponse.success && jsonResponse.data && jsonResponse.data.blogs) {
          blogsData = jsonResponse.data.blogs;
     
        } else {
          blogsData = jsonResponse;
        }
      } else {
        // Parse HTML response - backend returns rendered HTML page
        const htmlContent = await response.text();
        
        // Extract blog list from HTML
        blogsData = extractBlogListFromHTML(htmlContent);
      }
      
      if (blogsData && Array.isArray(blogsData)) {
        // Convert the blog data structure to match what our component expects
        const formattedBlogs = blogsData.map(blog => ({
          ...blog,
          status: blog.published ? 'published' : 'draft',
          author: blog.author || 'Admin'
        }));
        
        setBlogs(formattedBlogs);
      }
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract blog list from HTML
  const extractBlogListFromHTML = (html) => {
    // Look for blog articles in the HTML
    const blogMatches = html.match(/<article[^>]*class="[^"]*blog[^"]*"[^>]*>(.*?)<\/article>/gis) ||
                         html.match(/<div[^>]*class="[^"]*blog[^"]*"[^>]*>(.*?)<\/div>/gis);
    
    if (blogMatches) {
      return blogMatches.map((blogHTML, index) => {
        const titleMatch = blogHTML.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
        const linkMatch = blogHTML.match(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/i);
        const excerptMatch = blogHTML.match(/<p[^>]*class="[^"]*excerpt[^"]*"[^>]*>(.*?)<\/p>/i);
        const dateMatch = blogHTML.match(/<time[^>]*>(.*?)<\/time>/i);
        
        return {
          id: index + 1,
          title: titleMatch ? titleMatch[1].trim() : `Blog ${index + 1}`,
          slug: linkMatch ? linkMatch[1].replace('/blog/', '').replace('/', '') : `blog-${index + 1}`,
          excerpt: excerptMatch ? excerptMatch[1] : '',
          createdAt: dateMatch ? dateMatch[1] : new Date().toISOString(),
          author: 'Admin',
          status: 'published'
        };
      });
    }
    
    // Fallback: create empty array if no blogs found
    return [];
  };

  // Get all unique tags
  const allTags = [...new Set(blogs.flatMap(blog => 
    blog.tags ? (Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim())) : []
  ))];

  // Filter and sort blogs
  const filteredBlogs = blogs
    .filter(blog => {
      const matchesSearch = (blog.title && blog.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (blog.excerpt && blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (blog.content && blog.content.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesTag = !selectedTag || 
                        (blog.tags && (Array.isArray(blog.tags) ? 
                          blog.tags.some(tag => tag && tag.toString().toLowerCase().includes(selectedTag.toLowerCase())) :
                          blog.tags.toString().toLowerCase().includes(selectedTag.toLowerCase())
                        ));
      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else {
        return a.title.localeCompare(b.title);
      }
    });

  // Pagination
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const truncateContent = (content, maxLength = 150) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > maxLength ? plainText.substring(0, maxLength) + '...' : plainText;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{
        backgroundImage: 'url(/img/bg-new2.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Blog</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
              Discover insights, tips, and stories from our team. Stay updated with the latest trends and news.
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-medium transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <Home className="w-5 h-5 mr-2" />
              Explore Our Platform
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div className="lg:w-48">
              <select
                value={selectedTag}
                onChange={(e) => {
                  setSelectedTag(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="lg:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date">Latest First</option>
                <option value="title">Title A-Z</option>
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(searchTerm || selectedTag) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                  Search: {searchTerm}
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedTag && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                  Tag: {selectedTag}
                  <button
                    onClick={() => {
                      setSelectedTag('');
                      setCurrentPage(1);
                    }}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-white">
            Showing {currentBlogs.length} of {filteredBlogs.length} blog posts
          </p>
        </div>

        {/* Blog Grid */}
        {currentBlogs.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No blog posts found</h3>
            <p className="text-white">
              {searchTerm || selectedTag 
                ? 'Try adjusting your search or filters' 
                : 'No blog posts available at the moment'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {currentBlogs.map((blog) => (
              <article key={blog.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                {/* Featured Image */}
                {blog.featuredImage && (
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={blog.featuredImage}
                      alt={blog.title}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                  </div>
                )}

                <div className="p-6">
                  {/* Tags */}
                  {blog.tags && blog.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',')).slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                        >
                          {typeof tag === 'string' ? tag.trim() : tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                    <Link 
                      to={`/blog/${blog.slug}`}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {blog.title}
                    </Link>
                  </h3>

                  {/* Excerpt */}
                  {blog.excerpt && (
                    <div className="text-gray-600 mb-4 line-clamp-3 prose prose-sm max-w-none">
                      <div dangerouslySetInnerHTML={{ __html: truncateContent(blog.excerpt) }} />
                    </div>
                  )}

                  {/* Meta */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(blog.createdAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {blog.author}
                      </span>
                    </div>
                  </div>

                  {/* Read More */}
                  <Link
                    to={`/blog/${blog.slug}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Read more
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded-lg border ${
                    currentPage === page
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
